import { sumBy } from "lodash-es";
import { Component } from "react";
import { connect } from "react-redux";

import { ActionButtonIntent, CalloutIntent } from "widgets";

import { getSavedItemTypeLabel, pluralizeWord } from "../../app-utils/string.js";
import type { ParentStatistic, SavedItemsListing } from "../../graphql.js";
import { loadParentStatistics } from "../../redux/modules/parent.js";
import { type IAppState } from "../../redux/reducer.js";
import { ConfirmationDialog } from "../core/overlays/Dialogs.js";
import { ENTITY_TYPE } from "@/settings/settings.ts";

interface IDeleteSavedItemConfirmationDialogOwnProps {
  itemsData: SavedItemsListing;
  selectedIds: number[];

  confirmCallback: () => Promise<any>;
  cancelCallback: () => void;
  confirmationMessage?: string;
}

interface IDeleteSavedItemConfirmationDialogAppStateProps {
  parentStatistics: ParentStatistic[];
}

interface IDeleteSavedItemConfirmationDialogDispatchProps {
  loadParentStatistics: (parentIds: number[]) => Promise<ParentStatistic>;
}

export interface IDeleteSavedItemConfirmationDialogProps
  extends IDeleteSavedItemConfirmationDialogAppStateProps,
    IDeleteSavedItemConfirmationDialogDispatchProps,
    IDeleteSavedItemConfirmationDialogOwnProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: IDeleteSavedItemConfirmationDialogOwnProps,
): IDeleteSavedItemConfirmationDialogAppStateProps {
  return {
    parentStatistics: state.parent.parentStatistics,
  };
}

function mapDispatchToProps(dispatch): IDeleteSavedItemConfirmationDialogDispatchProps {
  return {
    loadParentStatistics: (parentIds: number[]) => dispatch(loadParentStatistics(parentIds)),
  };
}

interface IDeleteSavedItemConfirmationDialogState {
  deletionError: string; // we could also use redux-form just for this purpose ; but this state prop is also useful for errors in parent statistics fetching
}

class DeleteSavedItemConfirmationDialogImpl extends Component<
  IDeleteSavedItemConfirmationDialogProps,
  IDeleteSavedItemConfirmationDialogState
> {
  constructor(props: IDeleteSavedItemConfirmationDialogProps) {
    super(props);

    this.state = {
      deletionError: null,
    };
  }

  componentDidMount() {
    if (this.isSampleParentType(this.props)) {
      this.props.loadParentStatistics(this.props.selectedIds).catch(this.props.cancelCallback); // an message is shown as in-app alert, we should directly close this dialog to prevent any uninformed deletion
    }
  }

  isSampleParentType = (props: IDeleteSavedItemConfirmationDialogProps) => {
    return props.itemsData.type === ENTITY_TYPE.SAMPLE_PARENT;
  };

  handleDelete = () => {
    this.setState({ deletionError: null });
    return this.props.confirmCallback().catch((error: Error) => this.setState({ deletionError: error.message }));
  };

  render() {
    const itemsData = this.props.itemsData;
    const itemCount: number = this.props.selectedIds.length;
    const typeLabel: string = pluralizeWord(getSavedItemTypeLabel(itemsData), itemCount);

    const sampleCount: number = sumBy(this.props.parentStatistics, "sampleCount");

    const parentDeleteMsg: string = this.isSampleParentType(this.props)
      ? `This action will also delete the ${sampleCount} ${pluralizeWord("sample", sampleCount)} that ${sampleCount > 1 ? "are" : "is"} linked to ${itemCount > 1 ? "these" : "this"} ${typeLabel}.`
      : null;

    return (
      <ConfirmationDialog
        headerProps={{
          title: `Delete ${typeLabel}`,
        }}
        mainActionButtonProps={{
          label: `Delete`,
          onPress: () => this.handleDelete(),
          intent: ActionButtonIntent.Danger,
        }}
        footerMessage={
          this.state.deletionError
            ? { message: this.state.deletionError }
            : this.props.confirmationMessage
              ? { message: this.props.confirmationMessage, intent: CalloutIntent.Info }
              : null
        }
        closeFn={this.props.cancelCallback}
      >
        <div>Are you sure you want to delete the selected {typeLabel}?</div>
        {parentDeleteMsg}
      </ConfirmationDialog>
    );
  }
}

export default connect<
  IDeleteSavedItemConfirmationDialogAppStateProps,
  IDeleteSavedItemConfirmationDialogDispatchProps,
  IDeleteSavedItemConfirmationDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(DeleteSavedItemConfirmationDialogImpl);

export { DeleteSavedItemConfirmationDialogImpl };
